import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HttpRequestStatus, LoadingContainer } from "./loadingContainer";
import useReportRows from "../hooks/useReportRows";
import { Report, ReportRow } from "../api/models/report.models";
import useEcomodClasses from "../hooks/useEcomodClasses";
import { isInteger } from "../utils/rowUtils";
import { getClassLabelFromAkeneoObject } from "../utils/akeneoUtils";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import styled from "styled-components";
import { useForm, Controller } from "react-hook-form";
import useReport from "../hooks/useReport";
import ReportFooter from "./reportFooter";
import { InfoDot } from "./infoDot";
import { InputNumber } from "primereact/inputnumber";
import {
  CreateReportRowPayload,
  UpdateReportRowPayload,
} from "../api/models/reportRow.models";
import { RowValuesWithEcomodClass } from "../api/models/rowValues.models";
import { InputInstructionsHover } from "./inputInstructionsHover";

// Simple type for light reporting form data
interface LightReportFormData {
  consumerPackaging: {
    [materialCode: string]: number | null;
  };
  businessPackaging: {
    [materialCode: string]: number | null;
  };
  consumerCups: {
    [materialCode: string]: number | null;
  };
  businessCups: {
    [materialCode: string]: number | null;
  };
}

const PaddedContainer = styled.div`
  padding: 2rem;
`;

const LightReportContainer = styled.div`
  & h3 {
    margin-bottom: 2rem;
    font-weight: 500;
    font-size: 1.2rem;
  }

  & strong {
    font-weight: 500;
  }
`;

const Labels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 8px 0;
  margin-right: 1rem;

  > div {
    min-width: 250px;
    max-width: 300px;
    white-space: wrap;
    text-align: right;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
`;

const InputContainer = styled.div`
  font-size: 0.8rem;
  min-height: 50px;
`;

const LabelInputContainer = styled.div`
  font-size: 0.8rem;
  min-height: 51px;
  max-height: 51px;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  width: 100%;
`;

const InputsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 8px 0;
`;

const Header = styled.div<{ $span: number }>`
  font-weight: 500;
  grid-column: span ${(props) => props.$span || 2};
  font-size: 0.9rem;
`;

const Break = styled.div<{ $span: number; $maxHeight?: string }>`
  grid-column: span ${(props) => props.$span || 2};
  max-height: ${(props) => props.$maxHeight || "auto"};
`;

const MaterialsContainer = styled.div`
  margin-top: 1rem;
`;

interface ReportFormProps {
  reportId?: number;
  reportData?: Report;
  isLoadingReport: boolean;
  handleNextStep: () => void;
}

const LightReportForm: React.FC<ReportFormProps> = ({
  reportId,
  isLoadingReport,
  reportData,
  handleNextStep,
}: ReportFormProps) => {
  const { t } = useTranslation();
  const [locale] = useUIStore(useShallow((state: UIState) => [state.locale]));
  const [showTotalWeightWarning, setShowTotalWeightWarning] = useState(false);

  const { ecomodClasses, isLoading: isLoadingEcomodClasses } =
    useEcomodClasses();

  const { updateExistingReport, isUpdatingReport } = useReport({ reportId });

  const {
    createReportRowData,
    isCreatingReportRow,
    updateReportRowData,
    isUpdatingReportRow,
  } = useReportRows();

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
    reset,
  } = useForm<LightReportFormData>({
    mode: "onBlur",
    defaultValues: {
      consumerPackaging: {},
      businessPackaging: {},
      consumerCups: {},
      businessCups: {},
    },
  });

  useEffect(() => {
    const subscription = watch((value) => {
      // Check total weight for consumer packaging
      let totalConsumerWeight = 0;
      for (const weight of Object.values(value.consumerPackaging || {})) {
        if (weight) {
          totalConsumerWeight += weight;
        }
      }

      // Check total weight for business packaging
      let totalBusinessWeight = 0;
      for (const weight of Object.values(value.businessPackaging || {})) {
        if (weight) {
          totalBusinessWeight += weight;
        }
      }

      const totalWeight = totalConsumerWeight + totalBusinessWeight;

      if (totalWeight > 50000) {
        setShowTotalWeightWarning(true);
      } else {
        setShowTotalWeightWarning(false);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setShowTotalWeightWarning]);

  const isLoading = isLoadingReport || isLoadingEcomodClasses;

  useEffect(() => {
    if (reportData?.rows && reportData.rows.length > 0 && ecomodClasses) {
      const firstRow = reportData.rows[0];

      const consumerPackagingValues: { [materialCode: string]: number | null } =
        {};
      const businessPackagingValues: { [materialCode: string]: number | null } =
        {};
      const consumerCupsValues: { [materialCode: string]: number | null } = {};
      const businessCupsValues: { [materialCode: string]: number | null } = {};

      // Extract values from firstRow and populate the form fields
      if (firstRow.values) {
        firstRow.values.forEach((rowValue) => {
          if (rowValue.supClass) {
            return; // Skip supClass entries - they have their own form
          }

          if (rowValue.isEmptyCup) {
            const materialCode = rowValue.ecoModulationClass?.code;
            if (materialCode && isInteger(materialCode)) {
              const value = rowValue.value || null;

              if (rowValue.businessUse) {
                businessCupsValues[materialCode] = value;
              } else {
                consumerCupsValues[materialCode] = value;
              }
            }
          } else {
            const materialCode = rowValue.ecoModulationClass?.code;
            if (materialCode && isInteger(materialCode)) {
              const value = rowValue.value || null;

              if (rowValue.businessUse) {
                businessPackagingValues[materialCode] = value;
              } else {
                consumerPackagingValues[materialCode] = value;
              }
            }
          }
        });
      }

      reset({
        consumerPackaging: consumerPackagingValues,
        businessPackaging: businessPackagingValues,
        consumerCups: consumerCupsValues,
        businessCups: businessCupsValues,
      });
    }
  }, [reportData, ecomodClasses, reset]);

  const findMatchingIdFromReportData = (
    materialCode: string,
    businessUse: boolean,
    isEmptyCup: boolean,
  ): number | undefined => {
    if (!reportData || !reportData.rows) return undefined;

    for (const row of reportData.rows) {
      if (row.values) {
        for (const value of row.values) {
          if (
            value.ecoModulationClass?.code === materialCode &&
            value.businessUse === businessUse &&
            value.isEmptyCup === isEmptyCup
          ) {
            return value.id;
          }
        }
      }
    }

    return undefined;
  };

  const generateRowFromData = (
    data: LightReportFormData,
  ): RowValuesWithEcomodClass[] => {
    let rows: RowValuesWithEcomodClass[] = [];
    // Populate consumer packaging values
    for (const [materialCode, value] of Object.entries(
      data.consumerPackaging,
    )) {
      if (
        !!value &&
        parseFloat(value.toString()) > 0 &&
        isInteger(materialCode)
      ) {
        const ecomodClass = ecomodClasses?.find(
          (cls) => cls.code === materialCode,
        );

        rows.push({
          id: findMatchingIdFromReportData(materialCode, false, false),
          ecoModulationClass: ecomodClass,
          businessUse: false,
          reusableClass: undefined,
          packagedAbroad: false,
          value: value,
          secMaterial: null,
          secValue: 0,
          secValueType: undefined,
          isEmptyCup: false,
        });
      }
    }

    // Populate business packaging values
    for (const [materialCode, value] of Object.entries(
      data.businessPackaging,
    )) {
      if (
        !!value &&
        parseFloat(value.toString()) > 0 &&
        isInteger(materialCode)
      ) {
        const ecomodClass = ecomodClasses?.find(
          (cls) => cls.code === materialCode,
        );

        rows.push({
          id: findMatchingIdFromReportData(materialCode, true, false),
          ecoModulationClass: ecomodClass,
          businessUse: true,
          reusableClass: undefined,
          packagedAbroad: false,
          value: value,
          secMaterial: null,
          secValue: 0,
          secValueType: undefined,
          isEmptyCup: false,
        });
      }
    }

    // Populate consumer cups values
    for (const [materialCode, value] of Object.entries(data.consumerCups)) {
      if (
        !!value &&
        parseFloat(value.toString()) > 0 &&
        isInteger(materialCode)
      ) {
        const ecomodClass = ecomodClasses?.find(
          (cls) => cls.code === materialCode,
        );

        rows.push({
          id: findMatchingIdFromReportData(materialCode, false, true),
          ecoModulationClass: ecomodClass,
          businessUse: false,
          reusableClass: undefined,
          packagedAbroad: false,
          value: value,
          secMaterial: null,
          secValue: 0,
          secValueType: undefined,
          isEmptyCup: true,
        });
      }

      // Populate business cups values
      for (const [materialCode, value] of Object.entries(data.businessCups)) {
        if (
          !!value &&
          parseFloat(value.toString()) > 0 &&
          isInteger(materialCode)
        ) {
          const ecomodClass = ecomodClasses?.find(
            (cls) => cls.code === materialCode,
          );

          rows.push({
            id: findMatchingIdFromReportData(materialCode, true, true),
            ecoModulationClass: ecomodClass,
            businessUse: true,
            reusableClass: undefined,
            packagedAbroad: false,
            value: value,
            secMaterial: null,
            secValue: 0,
            secValueType: undefined,
            isEmptyCup: true,
          });
        }
      }
    }

    return rows;
  };

  const createReportRowFromFormData = async (
    data: LightReportFormData,
  ): Promise<ReportRow[] | void> => {
    if (reportData?.rows && reportData.rows.length > 0) {
      const firstRow = reportData.rows[0];

      const rowPayload: UpdateReportRowPayload = {
        id: firstRow.id as number,
        reportId: reportId as number,
        ean: firstRow.ean,
        name: firstRow.name,
        amount: firstRow.amount,
        active: firstRow.active,
        values: firstRow
          ? firstRow.values
              .filter((val) => !!val.supClass)
              .concat(generateRowFromData(data)) // Values from SUP class form will be preserved
          : generateRowFromData(data),
      };

      const result = await updateReportRowData(rowPayload);
      return [result];
    } else {
      const rowPayload: CreateReportRowPayload = {
        reportId: reportId as number,
        ean: "",
        name: "Generic Material Row",
        amount: 1,
        active: true,
        values: generateRowFromData(data),
      };

      const result = await createReportRowData(rowPayload);
      return [result];
    }
  };

  const handleUpdateReport = async (data: LightReportFormData) => {
    if (!reportId) return;

    await createReportRowFromFormData(data);

    handleNextStep();
  };

  const handleNextStepClick = () => {
    handleSubmit(handleUpdateReport)();
  };

  const handleSaveDraft = async () => {
    if (!reportId) return;

    await createReportRowFromFormData(getValues());
  };

  const lightMaterials = ecomodClasses?.filter((ec) =>
    isInteger(ec.code as string),
  );

  // Helper function to render simplified materials grid for light reporting
  const renderLightMaterialsGrid = (
    packageType: "consumer" | "business",
    materials: typeof lightMaterials,
  ) => {
    if (!materials || materials.length === 0) return null;

    return (
      <Grid>
        {/* Labels column */}
        <Labels>
          <Header $span={1}>{t("label-materials")}</Header>

          {materials.map((material) => (
            <LabelInputContainer key={material.code}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <span>{getClassLabelFromAkeneoObject(locale, material)}</span>
              </div>
            </LabelInputContainer>
          ))}
        </Labels>

        {/* Input column */}
        <InputsGrid>
          <Header $span={1}>{t("header-light-report-amount-kg")}</Header>

          {materials.map((material) => (
            <InputContainer key={material.code} className="input-container">
              <Controller
                name={
                  packageType === "consumer"
                    ? (`consumerPackaging.${material.code}` as any)
                    : (`businessPackaging.${material.code}` as any)
                }
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <InputInstructionsHover
                    translationKey={
                      "input-instructions.light-report-" +
                      packageType +
                      "-" +
                      material.code
                    }
                  >
                    <InputNumber
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.value);
                      }}
                      value={field.value}
                      suffix=" kg"
                      placeholder="0"
                      locale="fi-FI"
                    />
                  </InputInstructionsHover>
                )}
              />
            </InputContainer>
          ))}
        </InputsGrid>
      </Grid>
    );
  };

  return (
    <LoadingContainer
      status={isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS}
    >
      <PaddedContainer>
        <LightReportContainer>
          <form>
            {/* Consumer Packaging Section */}
            <h3>
              <InfoDot
                position="right"
                text={t("infotext-consumer-packaging")}
                marginLeft="10px"
              >
                {t("header-consumer-packaging")}
              </InfoDot>
            </h3>
            <MaterialsContainer>
              {renderLightMaterialsGrid("consumer", lightMaterials)}

              {/* Empty Cups for Consumer */}
              <Grid style={{ marginTop: "2rem", marginLeft: "5px" }}>
                <Labels>
                  <Header $span={1}>{t("label-empty-cups")}</Header>
                  <LabelInputContainer>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <span>{t("label-paper-cups-paper")}</span>
                    </div>
                  </LabelInputContainer>
                  <LabelInputContainer>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <span>{t("label-paper-cups-plastic")}</span>
                    </div>
                  </LabelInputContainer>
                </Labels>

                <InputsGrid>
                  <Header $span={1}>
                    {t("header-light-report-amount-kg")}
                  </Header>
                  <InputContainer className="input-container">
                    <Controller
                      name="consumerCups.1"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          onChange={(e) => {
                            field.onChange(e.value);
                          }}
                          value={field.value}
                          suffix=" kg"
                          placeholder="0"
                          locale="fi-FI"
                        />
                      )}
                    />
                  </InputContainer>
                  <InputContainer className="input-container">
                    <Controller
                      name="consumerCups.2"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          onChange={(e) => {
                            field.onChange(e.value);
                          }}
                          value={field.value}
                          suffix=" kg"
                          placeholder="0"
                          locale="fi-FI"
                        />
                      )}
                    />
                  </InputContainer>
                </InputsGrid>
              </Grid>
            </MaterialsContainer>

            <br />

            {/* Business Packaging Section */}
            <h3>
              <InfoDot
                position="right"
                text={t("infotext-business-packaging")}
                marginLeft="10px"
              >
                {t("header-business-packaging")}
              </InfoDot>
            </h3>
            <MaterialsContainer>
              {renderLightMaterialsGrid("business", lightMaterials)}

              {/* Empty Cups for Business */}
              <Grid style={{ marginTop: "2rem" }}>
                <Labels>
                  <Header $span={1}>{t("label-empty-cups")}</Header>
                  <LabelInputContainer>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <span>{t("label-paper-cups-paper")}</span>
                    </div>
                  </LabelInputContainer>
                  <LabelInputContainer>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <span>{t("label-paper-cups-plastic")}</span>
                    </div>
                  </LabelInputContainer>
                </Labels>

                <InputsGrid>
                  <Header $span={1}>
                    {t("header-light-report-amount-kg")}
                  </Header>
                  <InputContainer className="input-container">
                    <Controller
                      name="businessCups.1"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          onChange={(e) => {
                            field.onChange(e.value);
                          }}
                          value={field.value}
                          suffix=" kg"
                          placeholder="0"
                          locale="fi-FI"
                        />
                      )}
                    />
                  </InputContainer>
                  <InputContainer className="input-container">
                    <Controller
                      name="businessCups.2"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          onChange={(e) => {
                            field.onChange(e.value);
                          }}
                          value={field.value}
                          suffix=" kg"
                          placeholder="0"
                          locale="fi-FI"
                        />
                      )}
                    />
                  </InputContainer>
                </InputsGrid>
              </Grid>
            </MaterialsContainer>
          </form>
        </LightReportContainer>
      </PaddedContainer>
      <ReportFooter
        saveDraft={handleSaveDraft}
        handleNextStep={handleNextStepClick}
        nextStepLabel={t("cmd-next")}
        proceedDisabled={showTotalWeightWarning}
        isLoading={
          isUpdatingReport || isCreatingReportRow || isUpdatingReportRow
        }
        displayEcomodError={showTotalWeightWarning}
        isLightReport={true}
      />
    </LoadingContainer>
  );
};

export default LightReportForm;
